import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutGrid,
  Inbox,
  AlertTriangle,
  Activity,
  MailCheck,
  Bot,
  Signal,
  ArrowLeft,
  Menu,
  ShieldCheck,
  Radio,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import { useSeo } from "@/hooks/useSeo";
import MetisConsole from "@/pages/metis/MetisConsole";

const OPERATOR = { name: "Chief Architect", role: "Root Operator", initials: "CA" };

// Links back into the Command Center's internal views (via router state) plus
// the active Métis route. Keeps a single source of navigation truth.
const ADMIN_NAV = [
  { id: "overview", label: "Command Overview", icon: LayoutGrid },
  { id: "leads", label: "Lead Signals", icon: Inbox },
  { id: "alerts", label: "System Alerts", icon: AlertTriangle },
  { id: "activity", label: "Activity Log", icon: Activity },
  { id: "diagnostics", label: "Email Diagnostics", icon: MailCheck },
];

function MetisSidebar({ className = "", mobile = false }) {
  return (
    <aside
      className={`${className} w-64 shrink-0 flex-col border-r border-[rgba(255,255,255,0.06)] bg-[color:var(--bg-1)] ${
        mobile ? "flex h-full" : ""
      }`}
    >
      <div className="flex items-center gap-2 px-5 py-5">
        <Logo showText={false} />
        <div className="leading-tight">
          <div className="font-display text-sm font-semibold text-white">Métis</div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-neon-g">Command Center</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2" aria-label="Command Center navigation">
        {ADMIN_NAV.map((n) => (
          <Link
            key={n.id}
            to="/admin"
            state={{ view: n.id }}
            data-testid={`metis-nav-${n.id}`}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-brand transition-all duration-300 hover:bg-[rgba(255,255,255,0.03)] hover:text-white"
          >
            <n.icon size={17} />
            {n.label}
          </Link>
        ))}

        <div className="my-2 border-t border-[rgba(255,255,255,0.06)]" />

        <Link
          to="/admin/metis"
          data-testid="metis-nav-metis"
          className="flex w-full items-center gap-3 rounded-xl border border-[color:var(--stroke-p)] bg-[rgba(160,32,240,0.06)] px-3 py-2.5 text-sm text-white shadow-[var(--glow-p)] transition-all duration-300"
        >
          <Bot size={17} className="text-neon-p" />
          Métis
          <span className="ml-auto rounded-full bg-[rgba(160,32,240,0.16)] px-2 py-0.5 text-[10px] font-semibold text-neon-p">
            AI
          </span>
        </Link>
      </nav>

      <div className="m-3 rounded-2xl glass p-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(57,255,20,0.12)] text-sm font-bold text-neon-g">
            {OPERATOR.initials}
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white">{OPERATOR.name}</div>
            <div className="flex items-center gap-1 text-[11px] text-neon-g">
              <ShieldCheck size={11} /> {OPERATOR.role}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-[rgba(255,255,255,0.08)] pt-3 text-[11px] text-muted-brand">
          <span className="flex items-center gap-1">
            <Radio size={11} className="text-neon-g" /> Session
          </span>
          <span>Live</span>
        </div>
      </div>
    </aside>
  );
}

export default function MetisPage() {
  useSeo({
    title: "Métis — AI Receptionist · Command Center",
    description: "Métis, the AI receptionist and business assistant inside the Métis Command Center.",
  });

  const [sessionStart] = useState(() => new Date());

  return (
    <div className="flex min-h-screen bg-[color:var(--bg-0)] noise text-white">
      <MetisSidebar className="hidden lg:flex" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-[rgba(255,255,255,0.06)] bg-[rgba(11,11,13,0.72)] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-lg glass lg:hidden"
                    aria-label="Open navigation"
                    data-testid="metis-mobile-menu-button"
                  >
                    <Menu size={17} />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-72 border-r border-[rgba(255,255,255,0.08)] bg-[color:var(--bg-1)] p-0"
                >
                  <MetisSidebar mobile />
                </SheetContent>
              </Sheet>

              <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
                <Link to="/admin" className="text-muted-brand transition-colors hover:text-white">
                  Métis Command Center
                </Link>
                <span className="text-muted-brand">/</span>
                <span className="font-medium text-white">Métis · AI Receptionist</span>
              </nav>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs text-neon-g sm:flex">
                <Signal size={13} /> Live · {sessionStart.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </span>
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 rounded-lg glass px-3 py-1.5 text-sm text-white transition-all hover:shadow-[var(--glow-g)]"
                data-testid="metis-back-to-cc"
              >
                <ArrowLeft size={15} className="text-muted-brand" />
                <span className="hidden sm:inline">Command Center</span>
              </Link>
            </div>
          </div>
        </header>

        <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <MetisConsole />
        </div>
      </div>

      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}
